<template>
  <PersonalizedComponentRenderer :key="experienceRenderKey" />
</template>

<script lang="tsx" setup>
import NinetailedPreviewLabel from './NinetailedPreviewLabel.vue';
import { useExperience } from '~/composables/ninetailed/useExperience';
import { useNinetailedComponentTracking } from '~/composables/ninetailed/useNinetailedComponentTracking';
import { useTrackedComponentRoot } from '~/composables/ninetailed/useTrackedComponentRoot';
import type {
  Baseline,
  ExperienceConfiguration,
  Reference
} from '@ninetailed/experience.js';
import { getCurrentInstance, ref, Teleport, watch } from 'vue';
import type { JSX } from 'vue/jsx-runtime';
import { isContentPage } from '~/utils/guards/isContentPage';
import { buildNinetailedExperienceRenderKey } from '~/utils/ninetailed/buildNinetailedExperienceRenderKey';
import { resolveMappedComponentForEntry } from '~/utils/ninetailed/resolveMappedComponentForEntry';

type ComponentDefintion = string | (() => JSX.Element); // Pass a string to be resolved (must be globally registered) or a rendering function

const resolvePassthroughProps = (
  passthroughProps?: Record<string, unknown> & {
    'v-bind'?: Record<string, unknown>;
  }
) => {
  const nestedBinding = passthroughProps?.['v-bind'];

  if (nestedBinding && typeof nestedBinding === 'object') {
    return nestedBinding;
  }

  return passthroughProps ?? {};
};

const props = defineProps<{
  baseline: Baseline;
  component: ComponentDefintion;
  experiences: ExperienceConfiguration<Reference>[];
  passthroughProps?: any;
}>();

const componentRegistry = getCurrentInstance()?.appContext.components;

const resolveRenderingComponent = (
  component: ComponentDefintion | undefined
) => {
  if (typeof component === 'string') {
    return componentRegistry?.[component] ?? component;
  }
  if (component) {
    return component;
  }
};

const experienceState = useExperience({
  baseline: props.baseline,
  experiences: props.experiences
});

const lastResolvedExperienceId = ref('none');

watch(
  () => experienceState.value.experience?.id,
  (experienceId) => {
    if (experienceId) {
      lastResolvedExperienceId.value = experienceId;
    }
  }
);

const isPageEntry = isContentPage(props.baseline);
const { componentInstanceRef, trackedElement } = useTrackedComponentRoot();
const hiddenSentinel = ref<HTMLElement | null>(null);

const trackingVariant = computed(() => {
  if (!experienceState.value.hasVariants) {
    return props.baseline;
  }

  if (experienceState.value.status === 'loading') {
    return props.baseline;
  }

  return experienceState.value.variant;
});

const isVariantHidden = computed(
  () =>
    experienceState.value.status === 'success' &&
    'hidden' in experienceState.value.variant &&
    experienceState.value.variant.hidden
);

const trackingElement = computed(() =>
  isVariantHidden.value ? hiddenSentinel.value : trackedElement.value
);

const shouldTrack = computed(
  () =>
    !isPageEntry &&
    Boolean(trackingElement.value) &&
    (!experienceState.value.hasVariants ||
      experienceState.value.status !== 'loading')
);

useNinetailedComponentTracking({
  element: trackingElement,
  enabled: shouldTrack,
  experience: computed(() => experienceState.value.experience),
  variant: trackingVariant,
  variantIndex: computed(() =>
    experienceState.value.hasVariants ? experienceState.value.variantIndex : 0
  )
});

const renderPreviewLabel = () => {
  if (isPageEntry) {
    return null;
  }

  return (
    <NinetailedPreviewLabel
      baseline={props.baseline}
      experience={experienceState.value.experience}
      target={trackedElement}
      variant={trackingVariant.value}
    />
  );
};

const renderHiddenSentinel = () => {
  if (!isVariantHidden.value || isPageEntry) {
    return null;
  }

  return (
    <Teleport to="body">
      <div
        ref={hiddenSentinel}
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: '0',
          height: '0',
          overflow: 'hidden',
          clipPath: 'inset(50%)'
        }}
      />
    </Teleport>
  );
};

const ExperienceRenderComponent = computed(() => {
  const state = experienceState.value;
  const entry =
    state.hasVariants && state.status !== 'loading' && !isVariantHidden.value
      ? state.variant
      : props.baseline;

  return resolveRenderingComponent(
    resolveMappedComponentForEntry(entry as never, props.component)
  );
});

const experienceRenderKey = computed(() => {
  const state = experienceState.value;

  return buildNinetailedExperienceRenderKey({
    hasVariants: state.hasVariants,
    status: state.status,
    baselineId: props.baseline.id,
    experienceId: state.experience?.id,
    lastResolvedExperienceId: lastResolvedExperienceId.value,
    variantId: state.variant.id,
    isVariantHidden: isVariantHidden.value
  });
});

const PersonalizedComponentRenderer = () => {
  const state = experienceState.value;
  const ExperienceComponent = ExperienceRenderComponent.value;

  if (!state.hasVariants) {
    return (
      <>
        {/* @ts-expect-error ExperienceComponent has no constructor and this is fine */}
        <ExperienceComponent
          ref={componentInstanceRef}
          {...resolvePassthroughProps(props.passthroughProps)}
          item={props.baseline}
          key={props.baseline.id}
        />
        {renderPreviewLabel()}
      </>
    );
  }

  if (isVariantHidden.value) {
    return (
      <>
        {renderHiddenSentinel()}
        {renderPreviewLabel()}
      </>
    );
  }

  const item = state.status === 'loading' ? props.baseline : state.variant;
  const isPersonalizedVariant =
    state.status === 'success' && state.isPersonalized;

  return (
    <>
      {/* @ts-expect-error ExperienceComponent has no constructor and this is fine */}
      <ExperienceComponent
        ref={componentInstanceRef}
        key={experienceRenderKey.value}
        {...resolvePassthroughProps(props.passthroughProps)}
        item={item}
        {...(isPersonalizedVariant
          ? {
              ninetailed: {
                isPersonalized: state.isPersonalized,
                audience: {
                  id: state.audience?.id || 'all visitors'
                }
              }
            }
          : {})}
      />
      {renderPreviewLabel()}
    </>
  );
};
</script>
